import { createContext, ReactNode, useContext, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { WorkflowState } from '@/types/workflows'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import { useSelectedWorkflowContext } from './SelectedWorkflowProvider'

type SelectedWorkflowStateContextValue = {
  selectedWorkflowState?: WorkflowState
  setWorkflowState: (workflowStateId: string) => void
}

const SelectedWorkflowStateContext =
  createContext<SelectedWorkflowStateContextValue>(
    {} as SelectedWorkflowStateContextValue,
  )

export const useSelectedWorkflowStateContext = () =>
  useContext(SelectedWorkflowStateContext)

type SelectedWorkflowStateProviderProps = {
  children: ReactNode
}

export default function SelectedWorkflowStateProvider({
  children,
}: SelectedWorkflowStateProviderProps) {
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const workflowStates = selectedWorkflow.workflow_states || []

  // Default to the first state that is not processing
  const defaultState = workflowStates.find(
    ({ code }) => code !== 'processing', // We skip processing
  ) as WorkflowState

  const {
    value: workflowStateIdInUrl,
    toggleValue: setWorkflowStateIdInUrl,
    resetValue,
    isInUrl,
  } = useStatefulSearchParam({
    param: 'workflow_state',
    defaultValue: defaultState?.id, // defaultState might be undefined if there are no states
  })

  const selectedWorkflowState = workflowStates.find(
    ({ id }) => id === workflowStateIdInUrl,
  )

  // If we have a selected workflow state, but its in a different workflow, switch to that workflow.
  useEffect(() => {
    if (
      searchParams.get('workflow_state') !== 'none' &&
      workflowStates.length &&
      !selectedWorkflowState &&
      defaultState?.id
    ) {
      setWorkflowStateIdInUrl(defaultState?.id)
    }
  }, [
    defaultState?.id,
    searchParams,
    selectedWorkflowState,
    setWorkflowStateIdInUrl,
    workflowStates.length,
  ])

  useEffect(() => {
    if (isInUrl && pathname.includes('settings')) {
      resetValue()
    }
  })

  return (
    <SelectedWorkflowStateContext.Provider
      value={{
        selectedWorkflowState,
        setWorkflowState: setWorkflowStateIdInUrl,
      }}
    >
      {children}
    </SelectedWorkflowStateContext.Provider>
  )
}
